import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Sparkles, ArrowLeft, ArrowRight, Heart, Users, CheckCircle2, Sparkle } from "lucide-react";
import {
  createSubscriptionSession, getSubscriptionStatus,
  createSubscriptionPortalSession,
  getSubscriberEmail, setSubscriberEmail,
} from "@/lib/api";
import SocialShareButtons from "@/components/earthpulse/SocialShareButtons";

const POLL_INTERVAL = 2000;
const MAX_POLLS = 30;

// ─────────────────────────────────────────────────────────────
// Subscribe hero — the buy page
// ─────────────────────────────────────────────────────────────
export default function SubscribePage() {
  const [email, setEmail] = useState(getSubscriberEmail());
  const [promoCode, setPromoCode] = useState("");
  const [showPromo, setShowPromo] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [params] = useSearchParams();
  const cancelled = params.get("cancelled") === "1";

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!/^\S+@\S+\.\S+$/.test(email)) {
      setError("Please enter a valid email");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      setSubscriberEmail(email);
      const { checkout_url } = await createSubscriptionSession({
        email, origin_url: window.location.origin,
        promo_code: promoCode.trim() || undefined,
      });
      window.location.assign(checkout_url);
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || "Could not open checkout");
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen relative z-10" data-testid="subscribe-page">
      <header className="border-b border-[color:var(--gold)]/15">
        <div className="mx-auto max-w-[1100px] px-6 md:px-10 py-6">
          <Link to="/" className="flex items-center gap-3 group w-fit" data-testid="subscribe-back">
            <ArrowLeft size={16} className="text-[color:var(--gold)] group-hover:-translate-x-0.5 transition-transform" />
            <span className="label-overline">Back to Earth Pulse</span>
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-[900px] px-6 md:px-10 py-14 md:py-20">
        {cancelled && (
          <div
            className="mb-8 rounded-2xl border border-[color:var(--gold)]/30 px-5 py-4 font-serif-italic text-[color:var(--parchment-soft)]"
            data-testid="subscribe-cancelled-notice"
          >
            No worries — checkout was cancelled. Whenever you're ready, tap the button below.
          </div>
        )}

        <div className="text-center max-w-2xl mx-auto stagger-in">
          <div className="label-overline">Unlimited cosmic readings</div>
          <h1
            data-testid="subscribe-hero-title"
            className="mt-5 font-serif-display text-[color:var(--parchment)] text-4xl md:text-6xl leading-[1.05] tracking-tight"
          >
            One membership. <span className="font-serif-italic text-[color:var(--gold)]">Every chart, forever.</span>
          </h1>
          <p className="mt-6 font-serif-italic text-[color:var(--parchment-soft)] text-lg md:text-xl leading-relaxed">
            £8.88 a month for unlimited Birth Charts and Compatibility readings.
            Screenshot &amp; share as many as your heart wants. Cancel any time.
          </p>
        </div>

        <div className="mt-14 grid md:grid-cols-2 gap-6 stagger-in">
          <PerkCard
            icon={Sparkles}
            title="Unlimited Cosmic Birth Charts"
            body="Full Sun / Moon / Rising reading with life-theme, career paths, notable people, gifts, growth edges and a keepsake natal wheel."
          />
          <PerkCard
            icon={Users}
            title="Unlimited Compatibility readings"
            body="Two charts, one honest map — where you spark, where you'll need patience. Beautiful synastry wheels you can save and share."
            accent="rose"
          />
        </div>

        <form
          onSubmit={onSubmit}
          className="mt-14 parchment-card rounded-3xl p-8 md:p-12 max-w-2xl mx-auto"
          data-testid="subscribe-form"
        >
          <div className="label-overline text-[color:var(--gold)]">Your email</div>
          <p className="mt-2 font-serif-italic text-[color:var(--text-secondary)] text-sm">
            We'll use this only to unlock your readings and send Stripe receipts. No spam.
          </p>
          <input
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            className="mt-5 w-full bg-transparent border-b border-[color:var(--gold)]/40 focus:border-[color:var(--gold)] outline-none font-serif-display text-[color:var(--parchment)] text-2xl md:text-3xl py-3 tracking-tight transition"
            data-testid="subscribe-email-input"
          />

          {error && (
            <div className="mt-4 text-sm text-[color:var(--rose-gold)]" data-testid="subscribe-error">
              {error}
            </div>
          )}

          <div className="mt-6 text-center">
            {!showPromo ? (
              <button
                type="button"
                onClick={() => setShowPromo(true)}
                className="label-overline text-[10px] text-[color:var(--gold)] hover:text-[color:var(--parchment)] transition"
                data-testid="subscribe-promo-toggle"
              >
                have a code? enter it here
              </button>
            ) : (
              <div className="text-left">
                <div className="label-overline text-[10px] text-[color:var(--gold)]">Promo code</div>
                <input
                  type="text"
                  value={promoCode}
                  onChange={(e) => setPromoCode(e.target.value)}
                  placeholder="e.g. FRIEND-XXXX or a free code"
                  autoCapitalize="characters"
                  className="mt-2 w-full bg-transparent border-b border-[color:var(--gold)]/40 focus:border-[color:var(--gold)] outline-none font-serif-display text-[color:var(--parchment)] text-lg py-2 tracking-widest uppercase transition"
                  data-testid="subscribe-promo-input"
                />
                <p className="mt-2 font-serif-italic text-[color:var(--text-muted)] text-xs">
                  100%-off codes skip Stripe entirely and unlock membership immediately.
                </p>
              </div>
            )}
          </div>

          <button
            type="submit"
            disabled={loading}
            className="mt-10 w-full inline-flex items-center justify-center gap-3 px-8 py-4 rounded-full bg-[color:var(--gold)] hover:bg-[color:var(--gold-soft)] disabled:opacity-60 text-[color:var(--bg-base)] text-lg font-medium transition"
            data-testid="subscribe-submit"
          >
            <Sparkle size={16} />
            {loading ? "Opening secure checkout…" : "Start membership · £8.88 / month"}
            <ArrowRight size={16} />
          </button>

          <p className="mt-6 font-serif-italic text-[color:var(--text-muted)] text-xs text-center">
            Secure checkout via Stripe · cancel anytime from your account page · no long-term commitment.
          </p>
        </form>

        <div className="mt-10 text-center">
          <Link
            to="/subscription"
            className="label-overline text-[10px] text-[color:var(--gold)] hover:text-[color:var(--parchment)] transition inline-flex items-center gap-2"
            data-testid="subscribe-manage-link"
          >
            already subscribed? manage your account <ArrowRight size={11} />
          </Link>
        </div>
      </main>
    </div>
  );
}

function PerkCard({ icon: Icon, title, body, accent }) {
  const color = accent === "rose" ? "var(--rose-gold)" : "var(--gold)";
  return (
    <div className="parchment-card rounded-2xl p-8">
      <Icon size={18} style={{ color }} />
      <div className="mt-4 font-serif-display text-[color:var(--parchment)] text-2xl tracking-tight">
        {title}
      </div>
      <p className="mt-3 font-sans-body text-[color:var(--parchment-soft)] leading-relaxed">
        {body}
      </p>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────
// Success page — polls until Stripe webhook flips status to active
// ─────────────────────────────────────────────────────────────
export function SubscriptionSuccessPage() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("polling");
  const [row, setRow] = useState(null);
  const navigate = useNavigate();
  const emailRef = useRef(getSubscriberEmail());

  useEffect(() => {
    let cancelled = false;
    (async () => {
      let attempts = 0;
      while (attempts < MAX_POLLS && !cancelled) {
        try {
          const r = await getSubscriptionStatus({
            email: emailRef.current,
            session_id: sessionId,
          });
          if (r?.active) {
            if (r.email) setSubscriberEmail(r.email);
            if (!cancelled) { setRow(r); setStatus("active"); }
            return;
          }
        } catch (_) { /* ignore transient */ }
        attempts += 1;
        await new Promise((res) => setTimeout(res, POLL_INTERVAL));
      }
      if (!cancelled) setStatus("timeout");
    })();
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <div className="min-h-screen flex items-center justify-center px-6" data-testid="subscription-success-page">
      <div className="text-center max-w-md">
        {status === "polling" && (
          <>
            <div className="mx-auto w-14 h-14 rounded-full border border-[color:var(--gold)]/50 flex items-center justify-center animate-pulse">
              <Sparkles size={22} className="text-[color:var(--gold)]" />
            </div>
            <h1 className="mt-8 font-serif-display text-3xl md:text-4xl text-[color:var(--parchment)] tracking-tight">
              Weaving your membership…
            </h1>
            <p className="mt-4 font-serif-italic text-[color:var(--text-secondary)]">
              Confirming with Stripe. A few seconds.
            </p>
          </>
        )}
        {status === "active" && (
          <>
            <CheckCircle2 size={44} className="text-[color:var(--gold)] mx-auto" />
            <h1 className="mt-6 font-serif-display text-3xl md:text-5xl text-[color:var(--parchment)] tracking-tight">
              You're in. Welcome.
            </h1>
            <p className="mt-4 font-serif-italic text-[color:var(--parchment-soft)] max-w-sm mx-auto">
              Your first reading is one tap away. Come back anytime — the sky, and every chart, are yours to explore.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row gap-3 justify-center">
              <button
                onClick={() => navigate("/birth-chart")}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[color:var(--gold)] hover:bg-[color:var(--gold-soft)] text-[color:var(--bg-base)] font-medium transition"
                data-testid="success-open-birth-chart"
              >
                <Sparkles size={14} /> Open your first Birth Chart
              </button>
              <button
                onClick={() => navigate("/compatibility")}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-[color:var(--rose-gold)]/50 text-[color:var(--rose-gold)] hover:bg-[color:var(--rose-gold)]/10 font-medium transition"
                data-testid="success-open-compat"
              >
                <Heart size={14} /> Try Compatibility
              </button>
            </div>
            {row?.current_period_end && (
              <p className="mt-8 font-serif-italic text-[color:var(--text-muted)] text-xs">
                Next renewal · {new Date(row.current_period_end).toLocaleDateString("en-GB",
                  { day: "numeric", month: "long", year: "numeric" })}
              </p>
            )}

            <div className="mt-12 pt-8 border-t border-[color:var(--gold)]/15">
              <div className="label-overline mb-3">Tell the world</div>
              <p className="font-serif-italic text-[color:var(--parchment-soft)] text-sm max-w-sm mx-auto mb-6">
                Share Earth Pulse with a friend who feels it too — one tap.
              </p>
              <SocialShareButtons
                url="https://earthpulse.store/"
                text="I just joined Earth Pulse — daily cosmic guidance for the ones who feel it first. ✨"
                testIdPrefix="success-share"
              />
            </div>
          </>
        )}
        {status === "timeout" && (
          <>
            <Sparkles size={26} className="text-[color:var(--gold)] mx-auto mb-4" />
            <h1 className="font-serif-display text-[color:var(--parchment)] text-2xl md:text-3xl mb-3">
              Almost there…
            </h1>
            <p className="font-serif-italic text-[color:var(--parchment-soft)] mb-6">
              Stripe is taking a moment to confirm. Refresh in a few seconds — your membership will appear.
            </p>
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[color:var(--gold)] hover:bg-[color:var(--gold-soft)] text-[color:var(--bg-base)] font-medium transition"
              data-testid="success-refresh"
            >
              Refresh
            </button>
          </>
        )}
      </div>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────
// Manage subscription — opens Stripe portal
// ─────────────────────────────────────────────────────────────
export function SubscriptionManagePage() {
  const [email, setEmail] = useState(getSubscriberEmail());
  const [row, setRow] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [portalLoading, setPortalLoading] = useState(false);

  const check = async (e) => {
    e && e.preventDefault();
    if (!email) return;
    setLoading(true);
    setError(null);
    try {
      const r = await getSubscriptionStatus({ email });
      setRow(r);
      setSubscriberEmail(email);
    } catch (err) {
      setError(err?.message || "Could not check status");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (email) check(null); // eslint-disable-line react-hooks/exhaustive-deps
  }, []); // once on mount

  const openPortal = async () => {
    setPortalLoading(true);
    try {
      const { portal_url } = await createSubscriptionPortalSession({
        email, origin_url: window.location.origin,
      });
      window.location.assign(portal_url);
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not open billing portal");
      setPortalLoading(false);
    }
  };

  const active = row?.active;
  const renewalDate = useMemo(() => {
    if (!row?.current_period_end) return null;
    return new Date(row.current_period_end).toLocaleDateString("en-GB",
      { day: "numeric", month: "long", year: "numeric" });
  }, [row]);

  return (
    <div className="min-h-screen relative z-10" data-testid="subscription-manage-page">
      <header className="border-b border-[color:var(--gold)]/15">
        <div className="mx-auto max-w-[1100px] px-6 md:px-10 py-6">
          <Link to="/" className="flex items-center gap-3 group w-fit">
            <ArrowLeft size={16} className="text-[color:var(--gold)]" />
            <span className="label-overline">Back to Earth Pulse</span>
          </Link>
        </div>
      </header>
      <main className="mx-auto max-w-[720px] px-6 md:px-10 py-14 md:py-20">
        <div className="label-overline">Your membership</div>
        <h1 className="mt-4 font-serif-display text-[color:var(--parchment)] text-4xl md:text-5xl tracking-tight">
          Manage your Earth Pulse membership
        </h1>

        <form onSubmit={check} className="mt-10 parchment-card rounded-2xl p-8">
          <label className="label-overline text-[color:var(--gold)]" htmlFor="mgr-email">Email on your subscription</label>
          <input
            id="mgr-email"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="mt-4 w-full bg-transparent border-b border-[color:var(--gold)]/40 focus:border-[color:var(--gold)] outline-none font-serif-display text-[color:var(--parchment)] text-2xl py-3 transition"
            data-testid="manage-email-input"
            placeholder="you@example.com"
          />
          <button
            type="submit"
            disabled={loading}
            className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-[color:var(--gold)]/60 text-[color:var(--parchment)] hover:bg-[color:var(--gold)]/10 transition"
            data-testid="manage-check-btn"
          >
            {loading ? "Checking…" : "Check status"}
          </button>
        </form>

        {row && (
          <div className="mt-8 parchment-card rounded-2xl p-8" data-testid="manage-status">
            {active ? (
              <>
                <div className="flex items-start gap-3">
                  <CheckCircle2 size={22} className="text-[color:var(--gold)] mt-1" />
                  <div>
                    <div className="font-serif-display text-[color:var(--parchment)] text-2xl tracking-tight">
                      Membership active
                    </div>
                    <div className="mt-2 font-serif-italic text-[color:var(--parchment-soft)]">
                      {row.status === "trialing" ? "You're in your trial period. " : ""}
                      {renewalDate ? `Next renewal · ${renewalDate}. ` : ""}
                      {row.cancel_at_period_end ? "You've cancelled — access continues until the date above." : "Cancel anytime from Stripe's billing portal below."}
                    </div>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={openPortal}
                  disabled={portalLoading}
                  className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[color:var(--gold)] hover:bg-[color:var(--gold-soft)] text-[color:var(--bg-base)] font-medium transition"
                  data-testid="manage-portal-btn"
                >
                  {portalLoading ? "Opening…" : "Open Stripe billing portal"}
                </button>
              </>
            ) : (
              <>
                <div className="font-serif-display text-[color:var(--parchment)] text-xl tracking-tight">
                  We couldn't find an active membership for this email.
                </div>
                <p className="mt-3 font-serif-italic text-[color:var(--parchment-soft)]">
                  Start one below — you'll get unlimited readings for £8.88/month.
                </p>
                <Link
                  to="/subscribe"
                  className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[color:var(--gold)] hover:bg-[color:var(--gold-soft)] text-[color:var(--bg-base)] font-medium transition"
                  data-testid="manage-start-btn"
                >
                  Start membership <ArrowRight size={14} />
                </Link>
              </>
            )}
          </div>
        )}

        {error && (
          <div className="mt-6 text-sm text-[color:var(--rose-gold)]" data-testid="manage-error">{error}</div>
        )}
      </main>
    </div>
  );
}
